import { useState, useEffect, useMemo, useCallback } from "react";
import { Table } from "antd";
import { Resizable } from "react-resizable";

import "../../styles/Table.css";
import "../../styles/ResizableTable.css";

interface DataTableProps {
  dataSource?: any;
  columns?: any;
  pagination?: any;
  loading?: any;
  onChange?: any;
  /**
   * When set, the table persists column widths, sort, and filters in
   * sessionStorage under this key and makes columns resizable.
   */
  persistKey?: string;
  /**
   * Ant Design Table scroll. Defaults to `{ x: "max-content" }`.
   * Pass `undefined` / `false` to disable forced horizontal scroll.
   */
  scroll?: any;
}

const ResizableTitle = (props: any) => {
  const { onResize, width, ...restProps } = props;
  if (!width) return <th {...restProps} />;
  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => e.stopPropagation()}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

const DEFAULT_COL_WIDTH = 160;

const DataTable = (props: DataTableProps) => {
  const {
    dataSource,
    columns,
    pagination,
    loading,
    onChange,
    persistKey,
  } = props;
  const scroll =
    "scroll" in props ? props.scroll : { x: "max-content" };
  const enableScroll = Boolean(scroll);

  const widthsKey = persistKey ? `${persistKey}-widths` : undefined;
  const sortKey = persistKey ? `${persistKey}-sort` : undefined;
  const filtersKey = persistKey ? `${persistKey}-filters` : undefined;

  const [dataTableState, setDataTableState] = useState({
    pagination: { ...pagination },
  });

  const [widths, setWidths] = useState<Record<string, number>>(() => {
    if (!widthsKey) return {};
    try {
      return JSON.parse(sessionStorage.getItem(widthsKey) || "{}");
    } catch {
      return {};
    }
  });

  const persistedSort = useMemo(() => {
    if (!sortKey) return null;
    try {
      return JSON.parse(sessionStorage.getItem(sortKey) || "null");
    } catch {
      return null;
    }
  }, [sortKey]);

  const persistedFilters = useMemo(() => {
    if (!filtersKey) return null;
    try {
      return JSON.parse(sessionStorage.getItem(filtersKey) || "null");
    } catch {
      return null;
    }
  }, [filtersKey]);

  useEffect(() => {
    setDataTableState((prev) => ({
      ...prev,
      pagination: { ...pagination },
    }));
  }, [pagination]);

  const handleResize = useCallback(
    (colKey: string) => (_: any, { size }: any) => {
      setWidths((prev) => {
        const next = { ...prev, [colKey]: size.width };
        if (widthsKey) {
          try {
            sessionStorage.setItem(widthsKey, JSON.stringify(next));
          } catch {
            /* ignore quota errors */
          }
        }
        return next;
      });
    },
    [widthsKey]
  );

  const colKeyOf = (c: any, idx: number) =>
    String(c.key ?? c.dataIndex ?? `col-${idx}`);

  const mergedColumns = useMemo(() => {
    if (!Array.isArray(columns)) return columns;
    return columns.map((col: any, idx: number) => {
      const key = colKeyOf(col, idx);
      const width =
        widths[key] ??
        col.width ??
        (persistKey && enableScroll ? DEFAULT_COL_WIDTH : undefined);
      const merged: any = { ...col };
      if (width != null) merged.width = width;

      if (persistedSort && persistedSort.columnKey === key && persistedSort.order) {
        merged.sortOrder = persistedSort.order;
        if (!merged.sorter && col.sorter !== false) {
          merged.sorter = true;
        }
      }

      if (persistedFilters && persistedFilters[key]) {
        merged.filteredValue = persistedFilters[key];
      }

      if (persistKey && width != null) {
        merged.onHeaderCell = (column: any) => ({
          width: column.width,
          onResize: handleResize(key),
        });
      }
      return merged;
    });
  }, [
    columns,
    widths,
    persistKey,
    persistedSort,
    persistedFilters,
    handleResize,
    enableScroll,
  ]);

  const handleChange = (paginationArg: any, filters: any, sorter: any) => {
    setDataTableState((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, current: paginationArg.current },
    }));

    if (sortKey) {
      const s = Array.isArray(sorter) ? sorter[0] : sorter;
      const payload =
        s && s.order
          ? { columnKey: String(s.columnKey ?? s.field ?? ""), order: s.order }
          : null;
      try {
        sessionStorage.setItem(sortKey, JSON.stringify(payload));
      } catch {
        /* ignore */
      }
    }

    if (filtersKey) {
      const cleaned: Record<string, any> = {};
      Object.entries(filters || {}).forEach(([k, v]) => {
        if (v && Array.isArray(v) && v.length > 0) cleaned[k] = v;
      });
      try {
        sessionStorage.setItem(filtersKey, JSON.stringify(cleaned));
      } catch {
        /* ignore */
      }
    }

    if (onChange) onChange(paginationArg, filters, sorter);
  };

  return (
    <div
      className="table-border"
      style={enableScroll ? undefined : { overflowX: "hidden" }}
    >
      <Table
        size="middle"
        rowKey={(record) => record.id}
        columns={mergedColumns}
        dataSource={dataSource}
        pagination={{ ...dataTableState.pagination }}
        loading={loading}
        onChange={handleChange}
        {...(enableScroll ? { scroll } : {})}
        tableLayout={enableScroll ? undefined : "fixed"}
        components={
          persistKey && enableScroll
            ? { header: { cell: ResizableTitle } }
            : undefined
        }
      />
    </div>
  );
};

export default DataTable;
export type { DataTableProps };
